import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Landing Page
import LandingPage from "./pages/LandingPage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";

// Portal Pages
import PortalLayout from "./components/portal/PortalLayout";
import Dashboard from "./pages/portal/Dashboard";
import Products from "./pages/portal/Products";
import Categories from "./pages/portal/Categories";
import SalesOrders from "./pages/portal/SalesOrders";
import PurchaseOrders from "./pages/portal/PurchaseOrders";
import DeliveryOrders from "./pages/portal/DeliveryOrders";
import Invoices from "./pages/portal/Invoices";
import Inventory from "./pages/portal/Inventory";
import Customers from "./pages/portal/Customers";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-orange-600 rounded-full animate-spin"></div>
          <p className="text-slate-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/portal" replace />;
  }

  return children;
};

// Public Route - redirects to portal if authenticated
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-orange-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      {/* Portal Routes */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route
          path="categories"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route path="sales-orders" element={<SalesOrders />} />
        <Route
          path="purchase-orders"
          element={
            <ProtectedRoute allowedRoles={["admin", "sales"]}>
              <PurchaseOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="delivery"
          element={
            <ProtectedRoute allowedRoles={["admin", "sales", "warehouse"]}>
              <DeliveryOrders />
            </ProtectedRoute>
          }
        />
        <Route path="invoices" element={<Invoices />} />
        <Route
          path="inventory"
          element={
            <ProtectedRoute allowedRoles={["admin", "sales", "warehouse"]}>
              <Inventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="customers"
          element={
            <ProtectedRoute allowedRoles={["admin", "sales"]}>
              <Customers />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
